<script setup lang="ts">
import { z } from 'zod'
import type { FormErrorEvent, FormSubmitEvent } from '#ui/types'

const props = defineProps<{ collection: string, id: string, data?: any }>()

const emit = defineEmits(['created', 'updated'])
const toast = useToast()
const review = ref({ id: '', review: '', stars: 0, images: [] })
const fullCollection = computed(() => { if (props.collection) { return GetCollection(props.collection) } })

onMounted(() => {
  if (props.data?.id) {
    review.value = props.data
  }
})
const pb = useNuxtApp().$pb
const addingReview = ref(false)
type ReviewSchema = z.output<typeof reviewSchema>
const reviewSchema = z.object({
  id: z.string().optional(),
  review: z.string().min(3, 'Review is required'),
  stars: z.number().min(1, 'Enter your stars rating').max(5, 'Enter your star rating'),
  images: z.array(z.string().or(z.instanceof(File).optional())).nullable(),
})
const { displayError } = useSubmitError()
async function submitReview(event: FormSubmitEvent<ReviewSchema>) {
  const res = { ...event.data, [fullCollection.value.singular]: props.id }
  const { user } = useUser()
  toast.add({
    id: 'saving-review',
    title: 'Review Saving...',
    icon: 'ri:loader-5-fill',
  })
  const updated = { ...event.data, reviewer: user.value.name }
  try {
    if (!res.id) {
      await pb.collection('reviews').create(res)
      emit('created', updated)
    }
    else {
      await pb.collection('reviews').update(res.id, res)
      emit('updated', updated)
    }
    toast.remove('saving-review')
    toast.add({
      title: 'Review Saved...',
      icon: 'ri:check-line',
    })
    addingReview.value = false
  }
  catch (err) {
    toast.remove('saving-review')
    displayError(err)
  }
}
</script>

<template>
  <div
    v-if="review"
    class=""
  >
    <UForm
      :state="review"
      :schema="reviewSchema"
      class="p-3 bg-slate-50 md:p-8 lg:p-12 rounded shadow"
      @submit.prevent="submitReview"
    >
      <HeadingText tag="h4">
        Please enter your review
      </HeadingText>
      <p class="mb-6 mt-2">
        Lift the {{ fullCollection.singular }} with a glowing recommendation!
      </p>
      <hr class="my-3">

      <UFormGroup
        name="stars"
        class="mb-6"
        eager-validation
      >
        <button
          v-for="i in 5"
          :key="i"
          type="button"
          @click="review.stars = i"
        >
          <transition
            name="fade"
            mode="out-in"
          >
            <UIcon
              v-if="review.stars < i"
              name="ri:star-line"
              class="text-slate-500 w-6 h-6 md:h-8 lg:h-10 md:w-8 lg:w-10  hover:text-primary-600"
            />
            <UIcon
              v-else
              name="ri:star-fill"
              class="text-primary-600 w-6 h-6 md:h-8 lg:h-10 md:w-8 lg:w-10  hover:opacity-50"
            />
          </transition>
        </button>
      </UFormGroup>
      <UFormGroup
        name="review"
        label="Review"
        class="mb-6"
      >
        <UTextarea
          v-model="review.review"
          placeholder="Enter your review here..."
        />
      </UFormGroup>
      <UFormGroup
        v-slot="{ error }"
        label="Images"
        name="images"
        class="py-12"
      >
        <div
          class="bg-slate-200 p-6 rounded shadow-inner border border-transparent"
          :class="[error ? 'border-red-500' : '']"
        >
          <ImageUpload
            multiple
            :record-id="review.id"
            :collection-id="REVIEW_COLLECTION_ID"
            :images="review.images"
            @updated="review.images = $event"
          />
          <!-- <ImageFull v-for="image in updatedBusiness.gallery" :id="updatedBusiness.id" :key="image" :src="image" :collection-id="updatedBusiness.collectionId" /> -->
        </div>
      </UFormGroup>

      <div class="flex justify-end">
        <ButtonSave
          type="submit"
          label="Post Review"
          size="xl"
          class="mt-3"
        />
      </div>
    </UForm>
  </div>
</template>
